import React, { useState } from 'react';
import PrintInvoiceModal from './PrintInvoiceModal';
import { useAuth } from '../../contexts/AuthContext';
import { List } from 'react-window';
import { format } from 'date-fns';
import {
  MoreHorizontal, Eye, FileText, Edit, Link, Key, CircleDollarSign, CheckCircle,
  ToggleLeft, ToggleRight, Trash2, Calendar, AlertTriangle,
  MapPin, MapPinOff, Wifi, WifiOff, Radio, Sparkles, Printer, Settings2, Navigation,
} from 'lucide-react';
import WiFiQuickModal from './WiFiQuickModal';
import WaInactiveBadge from './WaInactiveBadge';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Estimated card height incl. vertical gap between cards.
// Card ~130-140px content + 12px bottom gap.

// Format durasi online relatif ("3 jam 12 menit", "2 hari 4 jam")
const formatOnlineDuration = (sessionStart) => {
  if (!sessionStart) return null;
  try {
    const start = new Date(sessionStart.replace(' ', 'T'));
    if (isNaN(start.getTime())) return null;
    const diffMs = Date.now() - start.getTime();
    if (diffMs < 0) return null;
    const totalMin = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (days > 0) return `${days} hari ${hours} jam`;
    if (hours > 0) return `${hours} jam ${mins} menit`;
    if (mins > 0) return `${mins} menit`;
    return 'baru saja';
  } catch { return null; }
};

const CARD_HEIGHT = 152;

// Threshold below which we skip virtualization (keeps DOM simple for small lists).
const VIRTUAL_THRESHOLD = 30;

/**
 * Inner card markup - rendered both in virtual and non-virtual modes.
 * Memoized so switching selection/ACS on other rows doesn't re-render unaffected cards.
 */
const CardBody = React.memo(function CardBody({
  customer,
  density,
  isSelected,
  acsData,
  branchName,
  handleSelectCustomer,
  handleEdit,
  handleDelete,
  handleResetPassword,
  handleTogglePaymentStatus,
  handleRecordPayment,
  handleToggleActive,
  handleDisableCustomer,
  handleEnableCustomer,
  openLinkModal,
  navigate,
  onViewDetail,
  handleMarkDebt,
  handleClearDebt,
  currentUserRole,
  handleRemotePPPoE,
  remotePPPoELoading,
  handleEnableWanWeb,
  enableWanWebLoading,
}) {
  const { user: _authUserHF } = useAuth();
  const _isTeknisi = (_authUserHF?.role || '').toLowerCase() === 'teknisi';
  const _isOwner = (_authUserHF?.role || '').toLowerCase() === 'owner';  // OWNER_MENU_GUARD_MOBILE_v1_2026_08_22
  const [printOpen, setPrintOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const model = acsData?.product_class || customer.ont_model;
  const rxPower = acsData?.rx_power || customer.ont_rx_power;
  const isOntOnline = acsData?.online || customer.acs_status === 'online';
  const rxVal = rxPower ? parseFloat(rxPower) : null;

  // 2026-06-02: density-aware spacing & font
  const isComfortable = density === 'comfortable';
  const cardPadCls = isComfortable ? 'px-4 py-4' : 'px-3 py-2.5';
  const nameFontCls = isComfortable ? 'font-semibold text-base' : 'font-semibold text-sm';
  const subFontCls = isComfortable ? 'text-xs' : 'text-[11px]';
  const dueFontCls = isComfortable ? 'text-sm' : 'text-xs';
  const rxColor = !isOntOnline || rxVal == null
    ? 'text-slate-400'
    : rxVal < -25 ? 'text-red-600'
    : rxVal < -20 ? 'text-amber-600'
    : 'text-emerald-600';
  const rxCritical = isOntOnline && rxVal != null && rxVal < -25;

  const payStatus = customer.payment_status_computed || 'BELUM LUNAS';

  // Due date logic — H+0/lewat=merah, H-1/H-2=oranye, H-3=hijau
  let dueText = '-';
  // 2026-06-02: better contrast on mobile — bold + indigo by default for far-due (H>=4)
  let dueClass = 'text-indigo-700 dark:text-indigo-300 font-bold';
  let dueBadge = null;
  if (customer.next_due_date) {
    const dueDate = new Date(customer.next_due_date);
    const today = new Date(); today.setHours(0,0,0,0);
    const dueStart = new Date(customer.next_due_date); dueStart.setHours(0,0,0,0);
    const daysLeft = Math.ceil((dueStart - today) / (1000*60*60*24));
    dueText = format(dueDate, 'dd/MM/yyyy');
    if (daysLeft < 0) { dueClass = 'text-red-700 dark:text-red-400 font-bold'; dueBadge = { text: `Telat ${Math.abs(daysLeft)}hr`, cls: 'bg-red-100 text-red-700' }; }
    else if (daysLeft === 0) { dueClass = 'text-red-700 dark:text-red-400 font-bold'; dueBadge = { text: 'Hari ini', cls: 'bg-red-100 text-red-700' }; }
    else if (daysLeft === 1) { dueClass = 'text-orange-700 dark:text-orange-400 font-bold'; dueBadge = { text: 'H-1', cls: 'bg-orange-100 text-orange-700' }; }
    else if (daysLeft === 2) { dueClass = 'text-orange-700 dark:text-orange-400 font-bold'; dueBadge = { text: 'H-2', cls: 'bg-orange-100 text-orange-700' }; }
    else if (daysLeft === 3) { dueClass = 'text-emerald-700 dark:text-emerald-400 font-bold'; dueBadge = { text: 'H-3', cls: 'bg-emerald-100 text-emerald-700' }; }
  }

  return (
    <>
    <div data-customer-row-id={customer.id} className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg ${cardPadCls} shadow-sm hover:shadow-md transition-all`}>
      {/* Row 1: checkbox, name+username, online badge, aksi */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => handleSelectCustomer(customer.id)}
          className="w-4 h-4 rounded border-slate-300 text-indigo-600 flex-shrink-0"
          data-testid={`mobile-select-${customer.id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {(() => {
              const lat = parseFloat(customer.latitude);
              const lng = parseFloat(customer.longitude);
              const hasMap = !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
              return hasMap ? (
                <a
                  href={`https://www.google.com/maps?q=${lat},${lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`mobile-map-badge-${customer.id}`}
                  title="Lokasi sudah di-set"
                  className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-emerald-600 hover:bg-emerald-50"
                >
                  <MapPin size={13} />
                </a>
              ) : (
                <span
                  data-testid={`mobile-map-badge-empty-${customer.id}`}
                  title="Koordinat belum di-set"
                  className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-amber-500"
                >
                  <MapPinOff size={13} />
                </span>
              );
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/app/customers/${customer.id}`); }}
              title="Klik untuk lihat detail pelanggan"
              data-testid={`mobile-view-detail-${customer.id}`}
              className={`${nameFontCls} text-slate-800 dark:text-slate-100 truncate leading-tight text-left hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline transition-colors cursor-pointer bg-transparent border-0 p-0 min-w-0`}
            >
              {customer.name}
            </button>
          </div>
          <div className={`${subFontCls} text-indigo-500 font-medium truncate leading-tight`}>{customer.pppoe_username || customer.account_number}</div>
        </div>
        {(() => {
          const isHotspot = (customer.connection_type || '').toLowerCase() === 'hotspot';
          if (customer.is_online) {
            const tip = (() => {
              const dur = formatOnlineDuration(customer.session_start);
              const base = isHotspot ? 'Online — Hotspot aktif' : 'Online — Wi-Fi terhubung';
              return dur ? `Online sejak ${dur} yang lalu` : base;
            })();
            return (
              <span
                title={tip}
                data-testid={`conn-online-${customer.id}`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wide border flex-shrink-0 ${isHotspot ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800' : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isHotspot ? 'bg-sky-500' : 'bg-green-500'} animate-pulse`}></span>
                ONLINE
              </span>
            );
          }
          return (
            <span
              title={isHotspot ? 'Offline — Hotspot tidak aktif' : 'Offline — Wi-Fi terputus'}
              data-testid={`conn-offline-${customer.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wide border flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500"></span>
              OFFLINE
            </span>
          );
        })()}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0 text-amber-500 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30" title="Aksi" data-testid={`mobile-actions-${customer.id}`}>
              <Sparkles className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {(model || rxPower) && (
              <div className="px-2 py-1.5 bg-teal-50 dark:bg-black border-b border-teal-200 dark:border-slate-700 rounded-t-md">
                <div className="text-[10px] text-teal-700 dark:text-teal-300 font-medium flex items-center justify-between">
                  <span className="truncate">{model || '-'}</span>
                  <span className={rxColor + ' font-bold ml-2'}>{isOntOnline && rxPower ? `${rxPower} dBm` : '-'}</span>
                </div>
              </div>
            )}
            <DropdownMenuItem onClick={() => (onViewDetail ? onViewDetail(customer.id) : navigate(`/app/customers/${customer.id}`))} data-testid={`mobile-row-view-detail-${customer.id}`}>
              <Eye className="w-4 h-4 mr-2 text-blue-500" />
              Lihat Detail
            </DropdownMenuItem>
            {/* 2026-07-30: Berhutang controls */}
            {(['admin','super_admin','kepala_cabang'].includes((currentUserRole||'').toLowerCase())) && (
              customer.debt_deadline ? (
                <DropdownMenuItem
                  onClick={() => handleClearDebt && handleClearDebt(customer)}
                  data-testid={`mobile-row-clear-debt-${customer.id}`}
                >
                  <span className="w-4 h-4 mr-2">💰</span> Hapus Masa Tenggang
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => handleMarkDebt && handleMarkDebt(customer)}
                  className="text-amber-600"
                  data-testid={`mobile-row-mark-debt-${customer.id}`}
                >
                  <span className="w-4 h-4 mr-2">🕐</span> Tandai Masa Tenggang
                </DropdownMenuItem>
              )
            )}
            {!_isTeknisi && (<>
            {!_isOwner && (
            <DropdownMenuItem onClick={() => navigate(`/app/invoices?customer=${encodeURIComponent(customer.account_number || customer.name)}`)}>
              <FileText className="w-4 h-4 mr-2 text-teal-500" />
              Edit Tagihan
            </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setPrintOpen(true)} data-testid={`mobile-row-print-invoice-${customer.id}`}>
              <Printer className="w-4 h-4 mr-2 text-indigo-500" />
              Print Invoice
            </DropdownMenuItem>
            {!_isOwner && (<>
            <DropdownMenuItem onClick={() => handleEdit(customer)}>
              <Edit className="w-4 h-4 mr-2 text-indigo-500" />
              Edit
            </DropdownMenuItem>
            {!customer.has_wan_device && (
              <DropdownMenuItem onClick={() => openLinkModal(customer)} className="text-blue-600">
                <Link className="w-4 h-4 mr-2" />
                Link ONT
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleResetPassword(customer)}>
              <Key className="w-4 h-4 mr-2 text-amber-500" />
              Reset Password
            </DropdownMenuItem>
            </>)}
            </>)}
            {/* Remote ONT (mirror of desktop menu) */}
            {handleRemotePPPoE && String(customer.connection_type || '').toLowerCase() === 'pppoe' && customer.is_online && (customer.active_pppoe_ip || customer.last_ip_address || customer.pppoe_username) && (
              <>
                {/* LOCAL_IP_2026-02-13: Akses lokal langsung tanpa proxy (untuk teknisi on-site di LAN/VPN) */}
                {(customer.active_pppoe_ip || customer.last_ip_address) && (
                  <DropdownMenuItem asChild>
                    <a
                      href={`http://${customer.active_pppoe_ip || customer.last_ip_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center w-full text-emerald-600 dark:text-emerald-400 cursor-pointer"
                      data-testid={`mobile-row-local-ont-${customer.id}`}
                      title={`Akses lokal (LAN/VPN): http://${customer.active_pppoe_ip || customer.last_ip_address}`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
                      </svg>
                      Akses Lokal
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleRemotePPPoE(customer)}
                  disabled={!!(remotePPPoELoading && remotePPPoELoading[customer.id])}
                  className="text-cyan-600 dark:text-cyan-400"
                  data-testid={`mobile-row-remote-ont-${customer.id}`}
                  title={`Buka web ONT via socat proxy (${customer.active_pppoe_ip || customer.last_ip_address || customer.pppoe_username})`}
                >
                  <Wifi className="w-4 h-4 mr-2" />
                  {(remotePPPoELoading && remotePPPoELoading[customer.id]) ? 'Membuka...' : 'Remote ONT'}
                </DropdownMenuItem>
              </>
            )}
            {!_isTeknisi && (<>
            {(() => {
              const _role = String(_authUserHF?.role || '').toLowerCase();
              const _isAdmin = _role === 'admin' || _role === 'super_admin' || _role === 'superadmin';
              const _isPrepaid = String(customer.payment_type || 'prepaid').toLowerCase() === 'prepaid';
              let _blocked = false, _openFrom = null, _daysLeft = 0;
              if (_isPrepaid && !_isAdmin && customer.next_due_date) {
                const _due = new Date(String(customer.next_due_date).slice(0,10) + 'T00:00:00');
                if (!isNaN(_due)) {
                  const _open = new Date(_due); _open.setDate(_open.getDate() - 5);
                  const _t = new Date(); _t.setHours(0,0,0,0);
                  if (_t < _open) {
                    _blocked = true; _openFrom = _open;
                    _daysLeft = Math.ceil((_open - _t) / (24*60*60*1000));
                  }
                }
              }
              const _title = _blocked
                ? `Pembayaran tersedia mulai ${_openFrom.toLocaleDateString('id-ID')} (${_daysLeft} hari lagi).`
                : 'Catat pembayaran';
              return (
                <DropdownMenuItem
                  onClick={() => { if (_blocked) { return; } handleRecordPayment(customer); }}
                  disabled={_blocked}
                  className={_blocked ? 'text-slate-400 cursor-not-allowed pointer-events-none' : 'text-emerald-600 font-medium'}
                  data-testid={`customer-mobile-bayar-${customer.id}`}
                  title={_title}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />Bayar{_blocked ? ` (${_daysLeft}h)` : ''}
                </DropdownMenuItem>
              );
            })()}
            {(!_isOwner || payStatus !== 'LUNAS') && (
            <DropdownMenuItem
              onClick={() => handleTogglePaymentStatus(customer)}
              className={payStatus === 'LUNAS' ? 'text-orange-600' : 'text-emerald-600'}
            >
              {payStatus === 'LUNAS' ? (
                <><CircleDollarSign className="w-4 h-4 mr-2" />Ubah ke Belum Bayar</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" />Ubah ke Lunas</>
              )}
            </DropdownMenuItem>
            )}
            </>)}
{!_isTeknisi && !_isOwner && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                const isActive = (customer.account_status !== 'disabled');
                if (isActive) {
                  if (window.confirm('Nonaktifkan customer? PPPoE akan diputus dan tidak bisa dial sampai diaktifkan kembali.')) {
                    handleDisableCustomer(customer.id);
                  }
                } else {
                  handleEnableCustomer(customer.id);
                }
              }}
              className={(customer.account_status !== 'disabled') ? 'text-orange-600' : 'text-emerald-600'}
            >
              {(customer.account_status !== 'disabled') ? (
                <><ToggleLeft className="w-4 h-4 mr-2" />Nonaktifkan</>
              ) : (
                <><ToggleRight className="w-4 h-4 mr-2" />Aktifkan</>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleDelete(customer.id)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Hapus
            </DropdownMenuItem>
            </>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row 2: key info */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 dark:text-slate-400">
        <span><span className="text-slate-400">Pemilik:</span> <span className="font-semibold text-slate-700 dark:text-slate-200">{customer.owner || '-'}</span></span>
        <span><span className="text-slate-400">Cabang:</span> <span className="font-semibold text-slate-700 dark:text-slate-200">{branchName}</span></span>
        <span><span className="text-slate-400">Paket:</span> <span className="font-semibold text-indigo-600">{customer.service_plan || '-'}</span></span>
        <span><span className="text-slate-400">Biaya:</span> <span className="font-bold text-emerald-600">Rp {(customer.monthly_fee || 0).toLocaleString('id-ID')}</span></span>
      </div>

      {/* Row 3 */}
      <div className="mt-2 flex items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`${dueFontCls} ${dueClass}`} title="Jatuh Tempo">
            <Calendar className="w-3 h-3 inline-block -mt-0.5 mr-0.5" />{dueText}
          </span>
          {dueBadge && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-semibold ${dueBadge.cls}`}>
              {dueBadge.text}
            </span>
          )}
          {(model || rxPower) && (
            <span className={`flex items-center gap-1 text-[10px] ${rxColor} font-semibold`} title={`Signal ONT: ${rxPower || 'N/A'} dBm`}>
              {rxCritical && <AlertTriangle className="w-3 h-3" />}
              {isOntOnline && rxPower ? `${rxPower} dBm` : 'N/A'}
            </span>
          )}
          {/* 2026-02: WiFi quick config button — hanya muncul jika customer punya device ACS */}
          {customer.has_wan_device && (customer.wan_device_id || customer.wan_device_serial) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setWifiOpen(true); }}
              data-testid={`wifi-config-btn-${customer.id}`}
              title="Ganti Nama & Password WiFi"
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 active:scale-95 transition-all"
            >
              <Settings2 className="w-3 h-3" />
              WiFi
            </button>
          )}
          {/* 2026-02: Petunjuk Arah (directions) — hanya muncul jika ada koordinat valid */}
          {(() => {
            const lat = parseFloat(customer.latitude);
            const lng = parseFloat(customer.longitude);
            const hasGeo = !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
            return hasGeo ? (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`directions-btn-${customer.id}`}
                title="Buka Petunjuk Arah ke Lokasi Pelanggan"
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 active:scale-95 transition-all"
              >
                <Navigation className="w-3 h-3" />
                Arah
              </a>
            ) : null;
          })()}
        </div>
        <div className="flex flex-col items-end gap-1">
        {payStatus === 'LUNAS' ? (
          <Badge className="bg-green-50 dark:bg-green-900/20 border border-green-500 text-green-600 dark:text-green-400 text-[9px] py-0.5 px-2 font-medium flex-shrink-0 min-w-[78px] justify-center">Lunas</Badge>
        ) : payStatus === 'TERLAMBAT' ? (
          <Badge className="bg-red-50 dark:bg-red-900/20 border border-red-500 text-red-600 dark:text-red-400 text-[9px] py-0.5 px-2 font-medium flex-shrink-0 min-w-[78px] justify-center">Terlambat</Badge>
        ) : (
          <Badge className="bg-amber-50 dark:bg-amber-900/20 border border-amber-500 text-amber-600 dark:text-amber-400 text-[9px] py-0.5 px-2 font-medium flex-shrink-0 min-w-[78px] justify-center">Belum Lunas</Badge>
        )}
        {(() => {
          const dd = customer.debt_deadline;
          if (!dd) return null;
          try {
            const _d = new Date(dd);
            const _today = new Date(); _today.setHours(0,0,0,0);
            if (isNaN(_d.getTime()) || _d < _today) return null;
            const _label = format(_d, 'dd MMM');
            return (
              <Badge
                data-testid={`mobile-debt-badge-${customer.id}`}
                title={`Masa tenggang s/d ${format(_d,'dd MMM yyyy')}`}
                className="bg-amber-50 dark:bg-amber-900/30 border border-amber-400 text-amber-800 dark:text-amber-200 text-[9px] py-0.5 px-2 font-semibold flex-shrink-0 whitespace-nowrap"
              >
                🕐 MASA TENGGANG s/d {_label}
              </Badge>
            );
          } catch (_e) { return null; }
        })()}
        <WaInactiveBadge customer={customer} compact />
        </div>
      </div>
    </div>
    <PrintInvoiceModal isOpen={printOpen} onClose={() => setPrintOpen(false)} customer={customer} />
    {wifiOpen && (
      <WiFiQuickModal
        isOpen={wifiOpen}
        onClose={() => setWifiOpen(false)}
        customer={customer}
        deviceId={customer.wan_device_id || customer.wan_device_serial}
      />
    )}
    </>
  );
});

/**
 * Row renderer for react-window virtualized list.
 * Receives `{ index, style, ...rowProps }` from <List>.
 */
function VirtualRow({ index, style, ...rowProps }) {
  const customer = rowProps.customers[index];
  const deviceKey = customer.wan_device_serial || customer.wan_device_id;
  return (
    <div style={{ ...style, paddingBottom: 12 }}>
      <CardBody
        density={rowProps.density}
        customer={customer}
        isSelected={rowProps.selectedIds.includes(customer.id)}
        acsData={deviceKey ? rowProps.acsDevicesStatus[deviceKey] : null}
        branchName={rowProps.getBranchName(customer)}
        handleSelectCustomer={rowProps.handleSelectCustomer}
        handleEdit={rowProps.handleEdit}
        handleDelete={rowProps.handleDelete}
        handleResetPassword={rowProps.handleResetPassword}
        handleTogglePaymentStatus={rowProps.handleTogglePaymentStatus}
        handleRecordPayment={rowProps.handleRecordPayment}
        handleToggleActive={rowProps.handleToggleActive}
        handleDisableCustomer={rowProps.handleDisableCustomer}
        handleEnableCustomer={rowProps.handleEnableCustomer}
        openLinkModal={rowProps.openLinkModal}
        navigate={rowProps.navigate}
        onViewDetail={rowProps.onViewDetail}
        handleMarkDebt={rowProps.handleMarkDebt}
        handleClearDebt={rowProps.handleClearDebt}
        currentUserRole={rowProps.currentUserRole}
        handleRemotePPPoE={rowProps.handleRemotePPPoE}
        remotePPPoELoading={rowProps.remotePPPoELoading}
        handleEnableWanWeb={rowProps.handleEnableWanWeb}
        enableWanWebLoading={rowProps.enableWanWebLoading}
      />
    </div>
  );
}

/**
 * Mobile card view for customers.
 * - If list size < VIRTUAL_THRESHOLD: renders all cards normally (simple DOM).
 * - If list size >= VIRTUAL_THRESHOLD: uses react-window for virtualization (only
 *   ~8-10 visible cards are in DOM at a time, regardless of total list size).
 */
const CustomerMobileCards = ({
  density = 'compact',
  isLoading,
  currentCustomers,
  acsDevicesStatus,
  branches,
  selectedCustomers,
  handleSelectCustomer,
  handleEdit,
  handleDelete,
  handleResetPassword,
  handleTogglePaymentStatus,
  handleRecordPayment,
  handleToggleActive,
  handleDisableCustomer,
  handleEnableCustomer,
  openLinkModal,
  navigate,
  onViewDetail,
  handleMarkDebt,
  handleClearDebt,
  currentUserRole,
  handleRemotePPPoE,
  remotePPPoELoading,
  handleEnableWanWeb,
  enableWanWebLoading,
}) => {
  if (isLoading) {
    return (
      <div className="block lg:hidden flex items-center justify-center py-12" data-testid="customers-mobile-cards">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (currentCustomers.length === 0) {
    return (
      <div className="block lg:hidden text-center py-12 text-slate-500" data-testid="customers-mobile-cards">
        Tidak ada data pelanggan
      </div>
    );
  }

  const getBranchName = (c) =>
    branches.find(b => b.id?.toString() === c.branch_id?.toString())?.name || c.branch_name || '-';

  // Small list → simple non-virtualized render (avoids virtualization overhead)
  if (currentCustomers.length < VIRTUAL_THRESHOLD) {
    return (
      <div className="block lg:hidden space-y-3" data-testid="customers-mobile-cards">
        {currentCustomers.map((customer) => {
          const deviceKey = customer.wan_device_serial || customer.wan_device_id;
          return (
            <CardBody
              key={customer.id}
              density={density}
              customer={customer}
              isSelected={selectedCustomers.includes(customer.id)}
              acsData={deviceKey ? acsDevicesStatus[deviceKey] : null}
              branchName={getBranchName(customer)}
              handleSelectCustomer={handleSelectCustomer}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleResetPassword={handleResetPassword}
              handleTogglePaymentStatus={handleTogglePaymentStatus}
              handleRecordPayment={handleRecordPayment}
              handleToggleActive={handleToggleActive}
              handleDisableCustomer={handleDisableCustomer}
              handleEnableCustomer={handleEnableCustomer}
              openLinkModal={openLinkModal}
              navigate={navigate}
              onViewDetail={onViewDetail}
              handleMarkDebt={handleMarkDebt}
              handleClearDebt={handleClearDebt}
              currentUserRole={currentUserRole}
              handleRemotePPPoE={handleRemotePPPoE}
              remotePPPoELoading={remotePPPoELoading}
              handleEnableWanWeb={handleEnableWanWeb}
              enableWanWebLoading={enableWanWebLoading}
            />
          );
        })}
      </div>
    );
  }

  // Large list → virtualized. Container takes max viewport height.
  // Height is calc'd from viewport to leave room for header/toolbar/pagination.
  const listHeight = Math.max(400, (typeof window !== 'undefined' ? window.innerHeight : 800) - 320);

  const rowProps = {
    customers: currentCustomers,
    density,
      selectedIds: selectedCustomers,
    acsDevicesStatus,
    getBranchName,
    handleSelectCustomer,
    handleEdit,
    handleDelete,
    handleResetPassword,
    handleTogglePaymentStatus,
    handleRecordPayment,
    handleToggleActive,
  handleDisableCustomer,
  handleEnableCustomer,
    openLinkModal,
    navigate,
    onViewDetail,
    handleMarkDebt,
    handleClearDebt,
    currentUserRole,
    handleRemotePPPoE,
    remotePPPoELoading,
    handleEnableWanWeb,
    enableWanWebLoading,
  };

  return (
    <div className="block lg:hidden" data-testid="customers-mobile-cards" data-virtualized="true">
      <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-1.5 px-1">
        <span>Mode virtual aktif ({currentCustomers.length} pelanggan)</span>
      </div>
      <List
        rowComponent={VirtualRow}
        rowCount={currentCustomers.length}
        rowHeight={CARD_HEIGHT}
        rowProps={rowProps}
        height={listHeight}
        width="100%"
        overscanCount={4}
        style={{
          maxHeight: listHeight,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
        }}
        className="scrollbar-hide"
      />
    </div>
  );
};

/**
 * Re-render only when data props change — handler ref changes from parent are ignored.
 */
const arePropsEqual = (prev, next) => (
  prev.isLoading === next.isLoading &&
  prev.currentCustomers === next.currentCustomers &&
  prev.acsDevicesStatus === next.acsDevicesStatus &&
  prev.branches === next.branches &&
  prev.selectedCustomers === next.selectedCustomers
);

export default React.memo(CustomerMobileCards, arePropsEqual);
